import React from 'react';
import { Plus, Users, User, Building, Search, Filter } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const UserManagementHeader = ({ 
  userType, 
  setUserType, 
  searchTerm, 
  setSearchTerm, 
  filterActive, 
  setFilterActive, 
  onCreateUser 
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  return (
    <div className="relative group">
      {/* Glass Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl'
        : 'glass-light-card border border-slate-200'
      } rounded-3xl ${isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'shadow-sm'}`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      } rounded-3xl`}></div>

      {/* Glass Particles */}
      <div className={`absolute bottom-8 left-12 w-1 h-1 ${isDarkMode ? 'bg-white/30 animate-ping' : 'hidden'} rounded-full`}></div>
      
      <div className="relative p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative group/icon">
              <div className={`${isDarkMode ? '' : 'hidden'} absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm`}></div>
              <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center border ${isDarkMode ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border-white/20' : 'bg-brand/10 border-brand/20'}`}>
                <Users className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                <div className={`${isDarkMode ? '' : 'hidden'} absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm`}></div>
              </div>
            </div>
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{t('admin.users.title')}</h2>
              <p className={`${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'}`}>{t('admin.users.subtitle')}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Liquid Glass Benutzertyp Switch */}
            <div className="relative group/switch">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-xl shadow-[0_4px_16px_rgba(0,0,0,0.2)]'
                : 'bg-slate-100 border border-slate-200'
              } rounded-2xl`}></div>
              <div className="relative flex items-center space-x-1 p-1 rounded-2xl">
                <button
                  onClick={() => setUserType('admin')}
                  className={`relative flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    userType === 'admin'
                      ? (isDarkMode ? 'text-white' : 'text-slate-900')
                      : (isDarkMode ? 'text-white/70 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                  }`}
                >
                  {userType === 'admin' && (
                    <div className={`absolute inset-0 rounded-xl ${isDarkMode ? 'bg-slate-600/40 backdrop-blur-xl border border-white/30 shadow-[0_4px_16px_rgba(0,0,0,0.3)]' : 'bg-white border border-slate-200 shadow-sm'}`}></div>
                  )}
                  <User className="relative w-4 h-4 mr-2" />
                  <span className="relative">{t('admin.users.localUsers')}</span>
                </button>
                <button
                  onClick={() => setUserType('oci')}
                  className={`relative flex items-center px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    userType === 'oci'
                      ? (isDarkMode ? 'text-white' : 'text-slate-900')
                      : (isDarkMode ? 'text-white/70 hover:text-white' : 'text-slate-600 hover:text-slate-900')
                  }`}
                >
                  {userType === 'oci' && (
                    <div className={`absolute inset-0 rounded-xl ${isDarkMode ? 'bg-slate-600/40 backdrop-blur-xl border border-white/30 shadow-[0_4px_16px_rgba(0,0,0,0.3)]' : 'bg-white border border-slate-200 shadow-sm'}`}></div>
                  )}
                  <Building className="relative w-4 h-4 mr-2" />
                  <span className="relative">{t('admin.users.ociUsers')}</span>
                </button>
              </div>
            </div>

            {/* Neuer Benutzer Button */}
            <button
              onClick={onCreateUser}
              className={`relative group/button overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white shadow-sm'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(59,130,246,0.7) 0%, rgba(37,99,235,0.7) 50%, rgba(29,78,216,0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              {/* Smooth Hover Overlay */}
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/button:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                } : {}}
              ></div>

              <div className={`relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'drop-shadow-lg group-hover/button:drop-shadow-xl' : ''}`}>
                <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/button:scale-110" />
                {userType === 'oci' ? t('admin.users.newOciUser') : t('admin.users.newUser')}
              </div>

              {/* Smooth Wave Effect */}
              <div className={`absolute inset-0 opacity-0 group-hover/button:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/button:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  } : {}}
                ></div>
              </div>
            </button>
          </div>
        </div>

        {/* Suchleiste und Filter */}
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative group/search">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>
              <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 z-10 transition-colors duration-300 ${isDarkMode ? 'text-white/60 group-focus-within/search:text-blue-300/80' : 'text-slate-400 group-focus-within/search:text-brand'}`} />
              <input
                type="text"
                placeholder={t('admin.users.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`relative w-full pl-12 pr-4 py-3 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                  ? 'text-white placeholder-white/60 focus:ring-2 focus:ring-blue-400/50 focus:shadow-[0_0_20px_rgba(59,130,246,0.4)]'
                  : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                }`}
              />
              <div className={`absolute inset-0 rounded-2xl border border-transparent transition-all duration-300 pointer-events-none ${isDarkMode ? 'group-focus-within/search:border-blue-400/50' : 'group-focus-within/search:border-brand/40'}`}></div>
            </div>
          </div>

          {userType === 'oci' && (
            <div className="flex items-center space-x-3">
              <Filter className={`w-4 h-4 ${isDarkMode ? 'text-white/60 drop-shadow-sm' : 'text-slate-400'}`} />
              <div className="relative group/filter">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                  className={`relative px-4 py-3 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 appearance-none cursor-pointer ${isDarkMode ? 'text-white focus:ring-2 focus:ring-blue-400/50 focus:shadow-[0_0_20px_rgba(59,130,246,0.4)]' : 'text-slate-900 focus:ring-2 focus:ring-brand/40'}`}
                >
                  <option value="all" className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-slate-900'}`}>{t('admin.users.filterAll')}</option>
                  <option value="active" className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-slate-900'}`}>{t('admin.users.filterActive')}</option>
                  <option value="inactive" className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-slate-900'}`}>{t('admin.users.filterInactive')}</option>
                </select>
                <div className={`absolute inset-0 rounded-2xl border border-transparent transition-all duration-300 pointer-events-none ${isDarkMode ? 'group-focus-within/filter:border-blue-400/50' : 'group-focus-within/filter:border-brand/40'}`}></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagementHeader;